import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { T, springs } from '../../lib/tokens';
import { Glass } from '../glass/Glass';

/**
 * Collapsible card with optional `preview` (visible when collapsed) and `accent`
 * (top-edge color bar like the iCloud Notes card's yellow stripe).
 */
export const Collapsible = ({ icon, iconBg, iconColor, title, subtitle, preview, accent, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Glass style={{ padding: 0, overflow: 'hidden' }} hover={false} specular={false}>
      {accent && (
        <div style={{ height: 4, background: accent, opacity: 0.85 }} />
      )}
      <div style={{ padding: open ? '20px 22px 22px' : (preview ? '16px 22px 18px' : '14px 22px') }}>
        <div
          onClick={() => setOpen(!open)}
          style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none' }}
        >
          <div style={{
            width: 30, height: 30, borderRadius: 9,
            background: iconBg || T.amberLight,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.6)',
          }}>
            {React.createElement(icon, { size: 15, color: iconColor || T.amber })}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.text, letterSpacing: '-0.01em' }}>{title}</div>
            {subtitle && <div style={{ fontSize: 11, color: T.textMuted, marginTop: 1 }}>{subtitle}</div>}
          </div>
          <motion.div
            animate={{ rotate: open ? 180 : 0 }}
            transition={springs.snappy}
          >
            <ChevronDown size={14} color={T.textMuted} />
          </motion.div>
        </div>

        {/* Preview text when collapsed */}
        {!open && preview && (
          <div style={{
            marginTop: 10,
            fontSize: 12, lineHeight: 1.5, color: T.textMid,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}>
            {preview}
          </div>
        )}

        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
              style={{ overflow: 'hidden' }}
            >
              <div style={{ marginTop: 16 }}>{children}</div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </Glass>
  );
};
